import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Avatar,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import Visibility from "@mui/icons-material/Visibility";
import { Banker } from "../../../validation/admin.validation";
import { formatPhoneNumber } from "../../../helper/phoneFormatter.helper";

interface Props {
  bankers: Banker[];
  loading: boolean;
  handleViewDetail: (banker: Banker) => void;
  onDelete?: (banker: Banker) => void;
  onEdit?: (banker: Banker) => void;
  onBlock?: (banker: Banker) => void;
}

const BankerTableView: React.FC<Props> = ({
  bankers,
  loading,
  handleViewDetail,
}) => {
  return (
    <>
      <div className="overflow-x-auto">
        <TableContainer
          component={Paper}
          sx={{ borderRadius: 3, minWidth: 600 }}
        >
          <Table sx={{ borderCollapse: "collapse" }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f9fafb" }}>
                <TableCell
                  sx={{ fontWeight: "bold", border: "1px solid #e5e7eb" }}
                >
                  Name
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", border: "1px solid #e5e7eb" }}
                >
                  Email
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", border: "1px solid #e5e7eb" }}
                >
                  Phone
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    border: "1px solid #e5e7eb",
                    textAlign: "center",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography className="text-gray-500">
                      Loading bankers...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : bankers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Box className="flex flex-col items-center justify-center py-32 text-center text-gray-500 bg-white rounded-lg border border-gray-200 mx-4">
                      <div className="flex items-center justify-center mb-4">
                        <div className="animate-pulse">
                          <SearchRoundedIcon
                            className="text-gray-400"
                            sx={{ fontSize: 48 }}
                          />
                        </div>
                      </div>
                      <Typography variant="h6" className="mt-4 text-gray-700">
                        No Bankers Found
                      </Typography>
                      <Typography variant="body2" className="text-gray-500">
                        You can try adjusting your search or wait for new banker
                        registrations.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                bankers.map((banker) => (
                  <TableRow key={banker.id} hover>
                    <TableCell
                      sx={{
                        border: "1px solid #e5e7eb",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: "#f3f4f6",
                          color: "#6b7280",
                          width: 32,
                          height: 32,
                        }}
                      >
                        <PersonIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {`${(banker as any).firstName || ''} ${(banker as any).middleName || ''} ${(banker as any).lastName || ''}`.trim()}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {banker.email}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ border: "1px solid #e5e7eb" }}>
                      {banker.phone ? formatPhoneNumber(banker.phone) : "-"}
                    </TableCell>
                    <TableCell
                      sx={{
                        border: "1px solid #e5e7eb",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 4,
                        }}
                      >
                        <Tooltip title="View Details">
                          <IconButton
                            color="info"
                            size="small"
                            onClick={() => handleViewDetail(banker)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </>
  );
};

export default BankerTableView;
